// Ingestion template executor.
//
// Walks an IngestionTemplate against the live DB state (via React Query
// cache) and builds the DiffState / ScopeDiffState objects the existing
// diff view consumes.  Only two operations exist: set_primitive and
// add_relation.  Freshness is deliberately out of scope — the diff view's
// freshness filter colours whatever records the template touched.

import type { QueryClient } from "@tanstack/react-query"
import { api } from "../../api/client"
import type {
  DiffCell,
  DiffRecord,
  DiffState,
  ExtractionEntry,
  LinkedRecordStub,
  PrimitiveType,
  RecordsPage,
  RelationDiff,
  RelationDiffEntry,
  ScopeDiffState,
} from "../../types"
import type {
  AddRelationWrite,
  IngestionResolution,
  IngestionTemplate,
  IngestionWrite,
  RelationRef,
  SetPrimitiveWrite,
} from "./types"

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export interface TemplateAnchor {
  scopeId: string
  recordId: string
  fileName: string
}

export interface TemplateExecutorContext {
  qc: QueryClient
}

export interface TemplateExecutorResult {
  /** Primary diff (anchor scope). Hand to enterDiffMode. */
  primary: DiffState
  /** Additional scopes with writes — push via setActionScopeDiff. */
  additional: Record<string, ScopeDiffState>
}

export async function executeIngestionTemplate(
  template: IngestionTemplate,
  anchor: TemplateAnchor,
  ctx: TemplateExecutorContext,
): Promise<TemplateExecutorResult> {
  if (anchor.scopeId !== template.anchorScopeId) {
    throw new Error(
      `Template ${template.id} expects anchor scope '${template.anchorScopeId}' but got '${anchor.scopeId}'`,
    )
  }
  const state = new ExecutorState(template, anchor, ctx)
  // Pre-resolve every declared resolution so writes can look them up by id.
  for (const res of template.resolutions) {
    await state.resolve(res)
  }
  for (const write of template.writes) {
    await dispatchWrite(write, state)
  }
  return state.finalize()
}

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

interface ScopeAccum {
  scopeId: string
  records: Record<string, DiffRecord>
  rawExtractions: ExtractionEntry[]
  anchoredRecords: DiffState["anchoredRecords"]
  relationDiffs: Record<string, Record<string, RelationDiff>>
}

type Resolved =
  | { kind: "found"; scopeId: string; recordId: string }
  | { kind: "missing"; scopeId: string; pkValue: string }

class ExecutorState {
  template: IngestionTemplate
  anchor: TemplateAnchor
  ctx: TemplateExecutorContext
  scopes: Map<string, ScopeAccum> = new Map()
  scopeMeta: Map<string, RecordsPage> = new Map()
  resolutions: Map<string, Resolved> = new Map()
  /** Synthetic record IDs per (scopeId, pk) so two writes share the same new record. */
  synthIdCache: Map<string, string> = new Map()

  constructor(
    template: IngestionTemplate,
    anchor: TemplateAnchor,
    ctx: TemplateExecutorContext,
  ) {
    this.template = template
    this.anchor = anchor
    this.ctx = ctx
    this.resolutions.set("anchor", {
      kind: "found",
      scopeId: anchor.scopeId,
      recordId: anchor.recordId,
    })
  }

  scope(scopeId: string): ScopeAccum {
    let s = this.scopes.get(scopeId)
    if (!s) {
      s = {
        scopeId,
        records: {},
        rawExtractions: [],
        anchoredRecords: [],
        relationDiffs: {},
      }
      this.scopes.set(scopeId, s)
    }
    return s
  }

  async loadScopeMeta(scopeId: string): Promise<RecordsPage> {
    const cached = this.scopeMeta.get(scopeId)
    if (cached) return cached
    const data = await this.ctx.qc.fetchQuery({
      queryKey: ["records", scopeId],
      queryFn: () => api.listRecords(scopeId),
      staleTime: 30_000,
    })
    this.scopeMeta.set(scopeId, data)
    return data
  }

  synthId(scopeId: string, pkValue: string): string {
    const key = `${scopeId}::${pkValue}`
    let id = this.synthIdCache.get(key)
    if (!id) {
      id = `new:${scopeId}:${pkValue}`
      this.synthIdCache.set(key, id)
    }
    return id
  }

  async resolve(res: IngestionResolution): Promise<Resolved> {
    if (this.resolutions.has(res.id)) return this.resolutions.get(res.id)!

    let resolved: Resolved
    if (res.kind === "anchor") {
      resolved = {
        kind: "found",
        scopeId: this.anchor.scopeId,
        recordId: this.anchor.recordId,
      }
    } else if (res.kind === "pk_match") {
      resolved = await resolvePkMatch(
        res.scopeId,
        res.pkPrimitive,
        res.value,
        this,
      )
    } else {
      resolved = await resolveTraverseFilter(res, this)
    }
    this.resolutions.set(res.id, resolved)
    return resolved
  }

  finalize(): TemplateExecutorResult {
    const primaryScope = this.scope(this.anchor.scopeId)
    const primary: DiffState = {
      scopeId: primaryScope.scopeId,
      fileName: this.anchor.fileName,
      records: primaryScope.records,
      rawExtractions: primaryScope.rawExtractions,
      anchoredRecords: primaryScope.anchoredRecords,
      ...(hasKeys(primaryScope.relationDiffs)
        ? { relationDiffs: primaryScope.relationDiffs }
        : {}),
    }
    const additional: Record<string, ScopeDiffState> = {}
    for (const [scopeId, s] of this.scopes) {
      if (scopeId === primary.scopeId) continue
      const hasWrites = s.rawExtractions.length > 0
      additional[scopeId] = {
        scopeId,
        fileName: this.anchor.fileName,
        records: s.records,
        rawExtractions: s.rawExtractions,
        anchoredRecords: s.anchoredRecords,
        ...(hasKeys(s.relationDiffs) ? { relationDiffs: s.relationDiffs } : {}),
        ...(!hasWrites ? { secondaryDiff: true } : {}),
      }
    }
    return { primary, additional }
  }
}

// ---------------------------------------------------------------------------
// Resolution
// ---------------------------------------------------------------------------

async function resolvePkMatch(
  scopeId: string,
  pkPrimitiveName: string,
  value: string,
  state: ExecutorState,
): Promise<Resolved> {
  const meta = await state.loadScopeMeta(scopeId)
  const pkPrim = meta.scope.primitives.find((p) => p.name === pkPrimitiveName)
  if (!pkPrim) return { kind: "missing", scopeId, pkValue: value }
  const hit = meta.records.find((r) => r.values[pkPrim.name]?.value === value)
  if (hit) return { kind: "found", scopeId, recordId: hit.id }
  return { kind: "missing", scopeId, pkValue: value }
}

async function resolveTraverseFilter(
  res: Extract<IngestionResolution, { kind: "traverse_filter" }>,
  state: ExecutorState,
): Promise<Resolved> {
  const seed = state.resolutions.get(res.seed)
  if (!seed || seed.kind !== "found") {
    return {
      kind: "missing",
      scopeId: res.targetScopeId,
      pkValue: res.filterValue,
    }
  }
  const stubs = await walkPath([seed.recordId], res.path, state)
  const hit = stubs.find(
    (stub) => stub.values[res.filterPrimitive]?.value === res.filterValue,
  )
  if (hit)
    return { kind: "found", scopeId: res.targetScopeId, recordId: hit.id }
  return {
    kind: "missing",
    scopeId: res.targetScopeId,
    pkValue: res.filterValue,
  }
}

async function walkPath(
  fromRecordIds: string[],
  path: RelationRef[],
  state: ExecutorState, // eslint-disable-line @typescript-eslint/no-unused-vars
): Promise<LinkedRecordStub[]> {
  if (path.length === 0) return []
  let currentIds = fromRecordIds
  let lastStubs: LinkedRecordStub[] = []
  for (const hop of path) {
    const collected: LinkedRecordStub[] = []
    const seen = new Set<string>()
    for (const id of currentIds) {
      try {
        const preview = await api.getRelationPreview(
          id,
          hop.relationPrimitiveName,
        )
        for (const stub of preview.records) {
          if (seen.has(stub.id)) continue
          seen.add(stub.id)
          collected.push(stub)
        }
      } catch (err) {
        console.warn("[template] relation preview failed", { id, hop, err })
      }
    }
    lastStubs = collected
    currentIds = collected.map((s) => s.id)
    if (currentIds.length === 0) return []
  }
  return lastStubs
}

// ---------------------------------------------------------------------------
// Writes
// ---------------------------------------------------------------------------

async function dispatchWrite(
  write: IngestionWrite,
  state: ExecutorState,
): Promise<void> {
  if (write.kind === "set_primitive") {
    await applySetPrimitive(write, state)
  } else {
    await applyAddRelation(write, state)
  }
}

async function applySetPrimitive(
  write: SetPrimitiveWrite,
  state: ExecutorState,
): Promise<void> {
  const resolved = state.resolutions.get(write.target)
  if (!resolved) return
  const resolution = state.template.resolutions.find(
    (r) => r.id === write.target,
  )
  const onMissing = resolutionOnMissing(resolution)

  const targetScopeId = write.primitive.scopeId
  const scopeMeta = await state.loadScopeMeta(targetScopeId)
  const primDef = scopeMeta.scope.primitives.find(
    (p) => p.name === write.primitive.primitiveName,
  )
  if (!primDef) return

  const scope = state.scope(targetScopeId)
  const pkPrim = scopeMeta.scope.primitives.find((p) => p.is_pk)
  const namePrim = scopeMeta.scope.primitives.find(
    (p) => !p.is_pk && p.type === "string" && p.is_visible,
  )

  if (resolved.kind === "found") {
    const record = scopeMeta.records.find((r) => r.id === resolved.recordId)
    const pv = record?.values[primDef.name]
    const dbValue = pv?.value ?? null
    const cell: DiffCell = {
      recordId: resolved.recordId,
      primitiveId: primDef.id,
      primitiveName: primDef.name,
      primitiveTitle: primDef.title,
      primitiveType: primDef.type as PrimitiveType,
      dbValue,
      fileValue: write.value,
      rawFileValue: write.value,
      typeIncompatible: false,
      accepted: null,
      lastHistorySource: pv?.source ?? null,
      lastHistoryTimestamp: pv?.updated_at ?? null,
    }
    mergeDiffRecord(scope, {
      recordId: resolved.recordId,
      pkValue:
        pkPrim && record
          ? (record.values[pkPrim.name]?.value ?? resolved.recordId)
          : resolved.recordId,
      nameValue:
        namePrim && record
          ? (record.values[namePrim.name]?.value ?? null)
          : null,
      isNew: false,
      cells: { [primDef.id]: cell },
    })
    mergeAnchoredRecord(scope, {
      record_id: resolved.recordId,
      pk_value:
        pkPrim && record
          ? (record.values[pkPrim.name]?.value ?? resolved.recordId)
          : resolved.recordId,
      name_value:
        namePrim && record
          ? (record.values[namePrim.name]?.value ?? null)
          : null,
      row_index: null,
      is_new: false,
    })
    scope.rawExtractions.push({
      record_id: resolved.recordId,
      primitive_id: primDef.id,
      primitive_scope_id: targetScopeId,
      primitive_title: primDef.title,
      primitive_type: primDef.type,
      value: write.value,
      raw_value: null,
      needs_cleaning: false,
      confidence: 1.0,
      extraction_type: "implicit",
      related_scope_id: null,
      accepted: false,
    })
    return
  }

  // Missing target.
  if (onMissing !== "create") return
  const synthId = state.synthId(targetScopeId, resolved.pkValue)
  const createCells: Record<string, DiffCell> = {}
  if (pkPrim) {
    createCells[pkPrim.id] = {
      recordId: synthId,
      primitiveId: pkPrim.id,
      primitiveName: pkPrim.name,
      primitiveTitle: pkPrim.title,
      primitiveType: pkPrim.type as PrimitiveType,
      dbValue: null,
      fileValue: resolved.pkValue,
      rawFileValue: resolved.pkValue,
      typeIncompatible: false,
      accepted: null,
    }
  }
  createCells[primDef.id] = {
    recordId: synthId,
    primitiveId: primDef.id,
    primitiveName: primDef.name,
    primitiveTitle: primDef.title,
    primitiveType: primDef.type as PrimitiveType,
    dbValue: null,
    fileValue: write.value,
    rawFileValue: write.value,
    typeIncompatible: false,
    accepted: null,
  }
  mergeDiffRecord(scope, {
    recordId: synthId,
    pkValue: resolved.pkValue,
    nameValue: null,
    isNew: true,
    cells: createCells,
  })
  mergeAnchoredRecord(scope, {
    record_id: synthId,
    pk_value: resolved.pkValue,
    name_value: null,
    row_index: null,
    is_new: true,
  })
  if (pkPrim) {
    scope.rawExtractions.push({
      record_id: synthId,
      primitive_id: pkPrim.id,
      primitive_scope_id: targetScopeId,
      primitive_title: pkPrim.title,
      primitive_type: pkPrim.type,
      value: resolved.pkValue,
      raw_value: resolved.pkValue,
      needs_cleaning: false,
      confidence: 1.0,
      extraction_type: "implicit",
      related_scope_id: null,
      accepted: false,
    })
  }
  scope.rawExtractions.push({
    record_id: synthId,
    primitive_id: primDef.id,
    primitive_scope_id: targetScopeId,
    primitive_title: primDef.title,
    primitive_type: primDef.type,
    value: write.value,
    raw_value: null,
    needs_cleaning: false,
    confidence: 1.0,
    extraction_type: "implicit",
    related_scope_id: null,
    accepted: false,
  })
}

async function applyAddRelation(
  write: AddRelationWrite,
  state: ExecutorState,
): Promise<void> {
  const from = state.resolutions.get(write.fromResolution)
  const to = state.resolutions.get(write.toResolution)
  if (!from || !to) return

  const fromRes = state.template.resolutions.find(
    (r) => r.id === write.fromResolution,
  )
  const toRes = state.template.resolutions.find(
    (r) => r.id === write.toResolution,
  )
  const fromOnMissing = resolutionOnMissing(fromRes)
  const toOnMissing = resolutionOnMissing(toRes)

  const fromRecordId = await ensureSideMaterialised(
    from,
    write.relation.fromScopeId,
    fromOnMissing,
    state,
  )
  const toRecordId = await ensureSideMaterialised(
    to,
    write.relation.toScopeId,
    toOnMissing,
    state,
  )
  if (!fromRecordId || !toRecordId) return

  const fromScopeMeta = await state.loadScopeMeta(write.relation.fromScopeId)
  const relPrim = fromScopeMeta.scope.primitives.find(
    (p) =>
      p.name === write.relation.relationPrimitiveName && p.type === "relation",
  )
  if (!relPrim) return

  const scope = state.scope(write.relation.fromScopeId)
  const toLabel = await labelForRecord(to, write.relation.toScopeId, state)

  const mirrorKey = `tpl:${state.template.id}:${write.id}:${fromRecordId}:${toRecordId}`
  const entry: RelationDiffEntry = {
    targetId: toRecordId,
    targetPkValue: toLabel.pkValue,
    targetNameValue: toLabel.nameValue,
    mappedId: null,
    action: "add",
    accepted: null,
    source: null,
    timestamp: null,
    mirrorKey,
  }

  const perRecord =
    scope.relationDiffs[fromRecordId] ??
    (scope.relationDiffs[fromRecordId] = {})
  const existing = perRecord[relPrim.id]
  if (existing) {
    existing.entries.push(entry)
  } else {
    perRecord[relPrim.id] = {
      recordId: fromRecordId,
      primitiveId: relPrim.id,
      primitiveName: relPrim.name,
      primitiveTitle: relPrim.title,
      relatedScopeId: write.relation.toScopeId,
      currentCount: 0,
      entries: [entry],
    }
  }

  scope.rawExtractions.push({
    record_id: fromRecordId,
    primitive_id: relPrim.id,
    primitive_scope_id: write.relation.fromScopeId,
    primitive_title: relPrim.title,
    primitive_type: "relation",
    value: toRecordId,
    raw_value: toLabel.pkValue,
    needs_cleaning: false,
    confidence: 1.0,
    extraction_type: "implicit",
    related_scope_id: write.relation.toScopeId,
    accepted: false,
    mapped_id: toLabel.pkValue,
    action: "add",
  })

  await mirrorRelationOntoToScope({
    fromRecordId,
    fromResolved: from,
    fromScopeId: write.relation.fromScopeId,
    toRecordId,
    toResolved: to,
    toScopeId: write.relation.toScopeId,
    state,
    mirrorKey,
  })
}

async function mirrorRelationOntoToScope(args: {
  fromRecordId: string
  fromResolved: Resolved
  fromScopeId: string
  toRecordId: string
  toResolved: Resolved
  toScopeId: string
  state: ExecutorState
  mirrorKey: string
}): Promise<void> {
  const {
    fromRecordId,
    fromResolved,
    fromScopeId,
    toRecordId,
    toResolved,
    toScopeId,
    state,
    mirrorKey,
  } = args
  const toScopeMeta = await state.loadScopeMeta(toScopeId)
  const inversePrim = toScopeMeta.scope.primitives.find(
    (p) => p.type === "relation" && p.related_scope_id === fromScopeId,
  )
  if (!inversePrim) return

  const toScope = state.scope(toScopeId)
  if (toResolved.kind === "found" && !toScope.records[toRecordId]) {
    const toRec = toScopeMeta.records.find((r) => r.id === toRecordId)
    const pkPrim = toScopeMeta.scope.primitives.find((p) => p.is_pk)
    const namePrim = toScopeMeta.scope.primitives.find(
      (p) => !p.is_pk && p.type === "string" && p.is_visible,
    )
    const pkValue =
      pkPrim && toRec
        ? (toRec.values[pkPrim.name]?.value ?? toRecordId)
        : toRecordId
    const nameValue =
      namePrim && toRec ? (toRec.values[namePrim.name]?.value ?? null) : null
    mergeDiffRecord(toScope, {
      recordId: toRecordId,
      pkValue,
      nameValue,
      isNew: false,
      cells: {},
    })
    mergeAnchoredRecord(toScope, {
      record_id: toRecordId,
      pk_value: pkValue,
      name_value: nameValue,
      row_index: null,
      is_new: false,
    })
  }

  const fromLabel = await labelForRecord(fromResolved, fromScopeId, state)
  const inverseEntry: RelationDiffEntry = {
    targetId: fromRecordId,
    targetPkValue: fromLabel.pkValue,
    targetNameValue: fromLabel.nameValue,
    mappedId: null,
    action: "add",
    accepted: null,
    source: null,
    timestamp: null,
    mirrorKey,
  }

  const toRec = toScopeMeta.records.find((r) => r.id === toRecordId)
  const existingTag = toRec?.relation_tags?.find(
    (t) => t.primitive_id === inversePrim.id,
  )
  const currentCount = existingTag?.count ?? 0

  const perRecord =
    toScope.relationDiffs[toRecordId] ??
    (toScope.relationDiffs[toRecordId] = {})
  const existing = perRecord[inversePrim.id]
  if (existing) {
    existing.entries.push(inverseEntry)
  } else {
    perRecord[inversePrim.id] = {
      recordId: toRecordId,
      primitiveId: inversePrim.id,
      primitiveName: inversePrim.name,
      primitiveTitle: inversePrim.title,
      relatedScopeId: fromScopeId,
      currentCount,
      entries: [inverseEntry],
    }
  }
}

async function ensureSideMaterialised(
  resolved: Resolved,
  scopeId: string,
  onMissing: "create" | "skip" | "fail",
  state: ExecutorState,
): Promise<string | null> {
  if (resolved.kind === "found") return resolved.recordId
  if (onMissing === "fail") {
    throw new Error(
      `Template resolution missing for scope '${scopeId}' (pk='${resolved.pkValue}')`,
    )
  }
  if (onMissing !== "create") return null
  const synthId = state.synthId(scopeId, resolved.pkValue)
  const scopeMeta = await state.loadScopeMeta(scopeId)
  const pkPrim = scopeMeta.scope.primitives.find((p) => p.is_pk)
  if (!pkPrim) return null
  const scope = state.scope(scopeId)
  if (!scope.records[synthId]) {
    mergeDiffRecord(scope, {
      recordId: synthId,
      pkValue: resolved.pkValue,
      nameValue: null,
      isNew: true,
      cells: {
        [pkPrim.id]: {
          recordId: synthId,
          primitiveId: pkPrim.id,
          primitiveName: pkPrim.name,
          primitiveTitle: pkPrim.title,
          primitiveType: pkPrim.type as PrimitiveType,
          dbValue: null,
          fileValue: resolved.pkValue,
          rawFileValue: resolved.pkValue,
          typeIncompatible: false,
          accepted: null,
        },
      },
    })
    mergeAnchoredRecord(scope, {
      record_id: synthId,
      pk_value: resolved.pkValue,
      name_value: null,
      row_index: null,
      is_new: true,
    })
    scope.rawExtractions.push({
      record_id: synthId,
      primitive_id: pkPrim.id,
      primitive_scope_id: scopeId,
      primitive_title: pkPrim.title,
      primitive_type: pkPrim.type,
      value: resolved.pkValue,
      raw_value: resolved.pkValue,
      needs_cleaning: false,
      confidence: 1.0,
      extraction_type: "implicit",
      related_scope_id: null,
      accepted: false,
    })
  }
  return synthId
}

async function labelForRecord(
  resolved: Resolved,
  scopeId: string,
  state: ExecutorState,
): Promise<{ pkValue: string; nameValue: string | null }> {
  if (resolved.kind === "missing")
    return { pkValue: resolved.pkValue, nameValue: null }
  const meta = await state.loadScopeMeta(scopeId)
  const rec = meta.records.find((r) => r.id === resolved.recordId)
  const pkPrim = meta.scope.primitives.find((p) => p.is_pk)
  const namePrim = meta.scope.primitives.find(
    (p) => !p.is_pk && p.type === "string" && p.is_visible,
  )
  return {
    pkValue:
      pkPrim && rec
        ? (rec.values[pkPrim.name]?.value ?? resolved.recordId)
        : resolved.recordId,
    nameValue:
      namePrim && rec ? (rec.values[namePrim.name]?.value ?? null) : null,
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function resolutionOnMissing(
  res: IngestionResolution | undefined,
): "create" | "skip" | "fail" {
  if (!res) return "skip"
  if (res.kind === "anchor") return "skip"
  return res.onMissing
}

function mergeDiffRecord(scope: ScopeAccum, next: DiffRecord): void {
  const existing = scope.records[next.recordId]
  if (!existing) {
    scope.records[next.recordId] = { ...next }
    return
  }
  existing.cells = { ...existing.cells, ...next.cells }
  if (next.isNew) existing.isNew = true
  if (next.nameValue && !existing.nameValue) existing.nameValue = next.nameValue
}

function mergeAnchoredRecord(
  scope: ScopeAccum,
  next: ScopeAccum["anchoredRecords"][number],
): void {
  const existing = scope.anchoredRecords.find(
    (a) => a.record_id === next.record_id,
  )
  if (!existing) {
    scope.anchoredRecords.push(next)
    return
  }
  if (next.is_new) existing.is_new = true
  if (next.name_value && !existing.name_value)
    existing.name_value = next.name_value
}

function hasKeys(o: object): boolean {
  return Object.keys(o).length > 0
}
